import { CompoundInterestParams, CompoundInterestYearResult, CompoundInterestSummary, MarketScenario } from './types';

/**
 * Calculate compound interest with regular deposits
 *
 * Formula:
 * FV = PV × (1 + r/n)^(n×t) + PMT × [((1 + r/n)^(n×t) - 1) / (r/n)]
 *
 * Where:
 * - FV = future value
 * - PV = present value (initial deposit)
 * - r = annual interest rate (decimal)
 * - n = compounding frequency per year
 * - t = time in years
 * - PMT = periodic payment amount
 */
export function calculateCompoundInterest(params: CompoundInterestParams): CompoundInterestYearResult[] {
    const {
        initialDeposit,
        regularDeposit,
        depositFrequency,
        interestRate,
        duration,
        compoundingFrequency,
        inflationRate,
        taxRate,
    } = params;

    const r = interestRate / 100;
    const i = inflationRate / 100;
    const n = compoundingFrequency;

    const periodsPerYear = depositFrequency === 'monthly' ? 12 : 52;
    const annualDeposit = regularDeposit * periodsPerYear;

    // Generate array of years [1, 2, 3, ..., duration]
    const years = Array.from({ length: duration }, (_, idx) => idx + 1);

    return years.map((year) => {
        const cumulativeDeposited = initialDeposit + annualDeposit * year;

        let totalValue: number;
        if (r === 0) {
            totalValue = cumulativeDeposited;
        } else {
            // Compound interest formula with regular deposits
            const compoundFactor = (1 + r / n) ** (n * year);

            // Future value of initial deposit
            const fvInitial = initialDeposit * compoundFactor;

            // Future value of annuity (regular deposits)
            // We need to adjust the payment to match the compounding frequency
            // Assumption: The total annual deposit is distributed evenly across compounding periods
            const paymentPerCompoundingPeriod = annualDeposit / n;

            // Formula: PMT × [((1 + r/n)^(n×t) - 1) / (r/n)]
            const fvAnnuity = paymentPerCompoundingPeriod * ((compoundFactor - 1) / (r / n));

            totalValue = fvInitial + fvAnnuity;
        }

        const interestEarned = totalValue - cumulativeDeposited;
        const taxAmount = interestEarned > 0 ? interestEarned * (taxRate / 100) : 0;
        const netValue = totalValue - taxAmount;
        const realValue = netValue / (1 + i) ** year;

        return {
            year,
            totalDeposited: cumulativeDeposited,
            interestEarned,
            totalValue,
            taxAmount,
            netValue,
            realValue,
        };
    });
}

/**
 * Calculate summary from yearly results
 */
export function calculateSummary(
    results: CompoundInterestYearResult[],
    params: CompoundInterestParams,
): CompoundInterestSummary {
    if (results.length === 0) {
        return {
            totalInvested: params.initialDeposit,
            finalValue: params.initialDeposit,
            totalProfit: 0,
            profitAfterTax: 0,
            taxAmount: 0,
            realValue: params.initialDeposit,
            roi: 0,
        };
    }

    const lastYear = results[results.length - 1];
    const totalProfit = lastYear.interestEarned;
    const profitAfterTax = totalProfit - lastYear.taxAmount;

    return {
        totalInvested: lastYear.totalDeposited,
        finalValue: lastYear.totalValue,
        totalProfit,
        profitAfterTax,
        taxAmount: lastYear.taxAmount,
        realValue: lastYear.realValue,
        roi: lastYear.totalDeposited > 0 ? (profitAfterTax / lastYear.totalDeposited) * 100 : 0,
    };
}

/**
 * Calculate results for multiple scenarios
 */
export function calculateScenarioResults(
    baseParams: CompoundInterestParams,
    scenarios: MarketScenario[],
): Map<string, CompoundInterestYearResult[]> {
    const scenarioResults = new Map<string, CompoundInterestYearResult[]>();

    scenarios.forEach((scenario) => {
        const scenarioParams = {
            ...baseParams,
            interestRate: scenario.interestRate,
            inflationRate: scenario.inflationRate,
        };
        scenarioResults.set(scenario.id, calculateCompoundInterest(scenarioParams));
    });

    return scenarioResults;
}
